"use client";

import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { areas, featured, problems, slugify, type AreaKey } from "./businessProblemsData";

/* ---------- scroll reveal (mirrors HomeReveal, scoped to .bp-page) ---------- */
export function BpReveal() {
  useEffect(() => {
    const root = document.querySelector(".bp-page");
    if (!root) return;
    root.classList.add("bp-js");
    const SEL = ".bp-reveal, .bp-stagger, .bp-card";
    const show = (el: Element) => el.classList.add("is-visible");

    if (!("IntersectionObserver" in window)) {
      root.querySelectorAll(SEL).forEach(show);
      return;
    }
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            show(e.target);
            io.unobserve(e.target);
          }
        }),
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );
    root.querySelectorAll(SEL).forEach((el) => io.observe(el));

    // Cards are re-rendered on search / filter / sort / expand: pick those up too
    const mo = new MutationObserver((muts) =>
      muts.forEach((m) =>
        m.addedNodes.forEach((n) => {
          if (!(n instanceof HTMLElement)) return;
          if (n.matches(SEL)) io.observe(n);
          n.querySelectorAll(SEL).forEach((el) => io.observe(el));
        })
      )
    );
    mo.observe(root, { childList: true, subtree: true });
    return () => { io.disconnect(); mo.disconnect(); };
  }, []);
  return null;
}

/* ---------- icons ---------- */
const S = { fill: "none", stroke: "currentColor", strokeWidth: 1.6, strokeLinecap: "round", strokeLinejoin: "round" } as const;
const Svg = ({ children, size = 34 }: { children: ReactNode; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 36 36" {...S} aria-hidden>{children}</svg>
);

const areaIcons: Record<AreaKey, ReactNode> = {
  revenue: <Svg><rect x="5" y="20" width="6" height="11" /><rect x="15" y="13" width="6" height="18" /><rect x="25" y="5" width="6" height="26" /></Svg>,
  margin: <Svg><ellipse cx="16" cy="9" rx="10" ry="4" /><path d="M6 9v7c0 2.2 4.5 4 10 4s10-1.8 10-4V9M6 16v7c0 2.2 4.5 4 10 4M26 20v3" /><circle cx="27" cy="27" r="5" /></Svg>,
  cash: <Svg><rect x="3" y="9" width="30" height="18" rx="2" /><circle cx="18" cy="18" r="5" /><path d="M8 14v0M28 22v0" /></Svg>,
  cost: <Svg><circle cx="18" cy="18" r="5" /><path d="M18 4v4M18 28v4M4 18h4M28 18h4M8 8l3 3M25 25l3 3M28 8l-3 3M11 25l-3 3" /></Svg>,
  inventory: <Svg><path d="M18 4l13 7v14l-13 7-13-7V11z" /><path d="M5 11l13 7 13-7M18 18v14" /></Svg>,
  procurement: <Svg><path d="M3 6h5l3 16h17l3-12H10" /><circle cx="14" cy="29" r="2" /><circle cx="26" cy="29" r="2" /><path d="M16 12l3 3 5-5" /></Svg>,
  supply: <Svg><path d="M2 8h20v17H2zM22 14h7l5 5v6H22" /><circle cx="9" cy="27" r="3" /><circle cx="27" cy="27" r="3" /></Svg>,
  assets: <Svg><path d="M26 4a7 7 0 00-7 9L5 27a3 3 0 004 4l14-14a7 7 0 009-7l-5 5-4-1-1-4z" /></Svg>,
  people: <Svg><circle cx="18" cy="9" r="4" /><circle cx="7" cy="14" r="3" /><circle cx="29" cy="14" r="3" /><path d="M10 31v-5a8 8 0 0116 0v5M2 26v-3a5 5 0 016-4M34 26v-3a5 5 0 00-6-4" /></Svg>,
  customer: <Svg><circle cx="18" cy="11" r="6" /><path d="M6 32c0-7 5-11 12-11s12 4 12 11" /></Svg>,
  risk: <Svg><path d="M18 3l12 5v9c0 8-5 13-12 16C11 30 6 25 6 17V8z" /><path d="M12 18l4 4 8-8" /></Svg>,
  esg: <Svg><path d="M6 30C6 14 16 6 32 5c0 16-7 25-22 25" /><path d="M6 30c4-8 9-13 16-17" /></Svg>,
};

const hl: ReactNode[] = [
  <svg key="a" width="26" height="26" viewBox="0 0 28 28" {...S}><circle cx="14" cy="8" r="4" /><circle cx="6" cy="20" r="3" /><circle cx="22" cy="20" r="3" /><path d="M11 11l-3 6M17 11l3 6M9 21h10" /></svg>,
  <svg key="b" width="26" height="26" viewBox="0 0 28 28" {...S}><circle cx="14" cy="12" r="6" /><path d="M11 21h6M12 24h4M14 2v2M3 12h2M23 12h2M6 4l1.5 1.5M22 4l-1.5 1.5" /></svg>,
  <svg key="c" width="26" height="26" viewBox="0 0 28 28" {...S}><circle cx="14" cy="14" r="10" /><path d="M14 8v6l4 3" /></svg>,
  <svg key="d" width="26" height="26" viewBox="0 0 28 28" {...S}><path d="M4 22l6-8 5 4 9-12" /><path d="M18 6h6v6" /></svg>,
  <svg key="e" width="26" height="26" viewBox="0 0 28 28" {...S}><circle cx="9" cy="8" r="3.5" /><circle cx="20" cy="9" r="3" /><path d="M2 22c0-4 3-7 7-7s7 3 7 7M16 17c1-1 2-1.5 4-1.5 3 0 6 2 6 6" /></svg>,
];

const Arrow = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M3 8h10M9 4l4 4-4 4" />
  </svg>
);

/* ---------- explorer ---------- */
type Sort = "default" | "az" | "za";
const INITIAL_COUNT = 6;

export default function BusinessProblems() {
  const [query, setQuery] = useState("");
  const [area, setArea] = useState<AreaKey | null>(null);
  const [sort, setSort] = useState<Sort>("default");
  const [showFilter, setShowFilter] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [idx, setIdx] = useState(0);

  const f = featured[idx];
  const filtering = query.trim() !== "" || area !== null;

  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    let out = problems.filter(
      (p) =>
        (!area || p.area === area) &&
        (!q || `${p.title} ${p.desc} ${p.tags.join(" ")}`.toLowerCase().includes(q))
    );
    if (sort === "az") out = [...out].sort((a, b) => a.title.localeCompare(b.title));
    if (sort === "za") out = [...out].sort((a, b) => b.title.localeCompare(a.title));
    return out;
  }, [query, area, sort]);

  const visible = filtering || expanded ? list : list.slice(0, INITIAL_COUNT);

  const pickArea = (k: AreaKey) => {
    setArea((cur) => (cur === k ? null : k));
    setExpanded(true);
    document.getElementById("all-problems")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      {/* AREAS */}
      <section className="bp-wrap bp-areas-section" id="explore-by-business-area">
        <div className="bp-areas-head bp-reveal">
          <h2>Explore Problems by Business Area</h2>
          <Link href="#all-problems" onClick={() => { setArea(null); setQuery(""); setExpanded(true); }}>
            View All Problems <Arrow />
          </Link>
        </div>
        <div className="bp-areas bp-stagger">
          {areas.map((a) => (
            <button
              key={a.key}
              type="button"
              className={`bp-area${area === a.key ? " is-active" : ""}`}
              onClick={() => pickArea(a.key)}
              aria-pressed={area === a.key}
            >
              <span className="bp-area-icon">{areaIcons[a.key]}</span>
              <span className="bp-area-label">{a.label[0]}<br />{a.label[1]}</span>
            </button>
          ))}
        </div>
      </section>

      {/* THIS WEEK */}
      <section className="bp-wrap bp-week-section bp-reveal">
        <div className="bp-week" key={f.id}>
          <div className="bp-week-main" >
            <p className="bp-week-label">THIS WEEK AT ORANE</p>
            <div className="bp-week-nav">
              <span className="bp-week-count">Problem {f.id}/52</span>
              <button type="button" aria-label="Previous problem" onClick={() => setIdx((idx + featured.length - 1) % featured.length)}>
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M13 8H3M7 4L3 8l4 4" /></svg>
              </button>
              <button type="button" aria-label="Next problem" onClick={() => setIdx((idx + 1) % featured.length)}>
                <Arrow />
              </button>
            </div>
            <h2>{f.title}</h2>
            <p>{f.question}</p>
            {/* <Link href={`/business-problems/${slugify(problems[f.id - 1].title)}`} className="bp-btn bp-btn-outline"> */}
            <Link href={``} className="bp-btn bp-btn-outline">
              Read the Insight <Arrow size={16} />
            </Link>
          </div>

          <div className="bp-week-side">
            <div className="bp-highlights">
              <h3 className="bp-eyebrow">KEY HIGHLIGHTS</h3>
              <ul>
                {f.highlights.map((h, i) => (
                  <li key={h}>
                    <span>{hl[i % hl.length]}</span>
                    {h}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bp-real-outcomes">
              <h3 className="bp-eyebrow">REAL OUTCOMES</h3>
              {f.outcomes.map(([n, l]) => (
                <div key={l}>
                  <strong>{n}</strong>
                  <span>{l}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ALL PROBLEMS */}
      <section className="bp-wrap bp-all" id="all-problems">
        <div className="bp-all-head bp-reveal">
          <h2 >All 52 Business Problems</h2>
          <div className="bp-tools">
            
            <button type="button" className={`bp-tool-btn${showFilter ? " is-open" : ""}`} onClick={() => setShowFilter((v) => !v)} aria-expanded={showFilter}>
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M3 5h14M5 10h10M8 15h4" /></svg>
              Filter
            </button>
            <label className="bp-sort">
              <select value={sort} onChange={(e) => setSort(e.target.value as Sort)} aria-label="Sort problems">
                <option value="default">Sort: 1–52</option>
                <option value="az">Sort: A-Z</option>
                <option value="za">Sort: Z-A</option>
              </select>
            </label>
          </div>
        </div>

        {showFilter && (
          <div className="bp-filter-panel" role="group" aria-label="Filter by business area">
            <button type="button" className={!area ? "is-active" : ""} onClick={() => setArea(null)}>All areas</button>
            {areas.map((a) => (
              <button key={a.key} type="button" className={area === a.key ? "is-active" : ""} onClick={() => setArea(area === a.key ? null : a.key)}>
                {a.label.join(" ")}
              </button>
            ))}
          </div>
        )}

        {filtering && (
          <p className="bp-result-count" aria-live="polite">
            Showing {list.length} of 52 problems
            <button type="button" onClick={() => { setQuery(""); setArea(null); }}>Clear</button>
          </p>
        )}

        {visible.length === 0 ? (
          <p className="bp-empty">No problems match that search. Try a broader term like &ldquo;inventory&rdquo; or &ldquo;margin&rdquo;.</p>
        ) : (
          <div className="bp-grid">
            {visible.map((p, i) => (
              <article className="bp-card" key={p.id} style={{ ["--d" as string]: `${(i % 6) * 0.07}s` }}>
                <Link href={``} className="bp-card-link" aria-label={p.title}>
                  <div className="bp-card-img" />
                  <div className="bp-card-body">
                    <span className="bp-card-no">{String(p.id).padStart(2, "0")} / 52</span>
                    <h3>{p.title}</h3>
                    <p>{p.desc}</p>
                    <div className="bp-card-foot">
                      <div className="bp-tags">
                        {p.tags.map((t) => <span key={t}>{t}</span>)}
                      </div>
                      <span className="bp-card-go"><Arrow /></span>
                    </div>
                  </div>
                </Link>
              </article>
            ))}
          </div>
        )}

        {!filtering && (
          <div className="bp-more">
            <button type="button" className="bp-btn bp-btn-ghost" onClick={() => setExpanded((v) => !v)}>
              {expanded ? "Show Fewer" : "View All 52 Problems"} <Arrow size={16} />
            </button>
          </div>
        )}
      </section>
    </>
  );
}
